import * as z from "zod";
import { userFacingError } from "../authorization";
import { syncAllFacets, writeCommitFile } from "../commitFiles";
import { DEFAULT_WORKFLOW_RETENTION_SECONDS } from "../executionRetention";
import { zql } from "../generatedDrizzleSchema";
import { sanitizeWorkflowName, uniqueWorkflowName } from "../workflowName";
import { mutator } from "./mutator";

export const importWorkflowSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]+$/),
  spaceId: z.string(),
  branchId: z.string(),
  commitId: z.string(),
  chatId: z.string(),
  systemEntryId: z.string(),
  name: z.string(),
  sourceFileName: z.string(),
  files: z.record(z.string(), z.string()),
  apps: z
    .array(
      z.object({
        stepDir: z.string(),
        name: z.string(),
        type: z.string(),
      })
    )
    .default([]),
});

export const importWorkflow = mutator({
  redactKeys: ["files"],
  schema: importWorkflowSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      id: workflowId,
      space,
      branchId,
      commitId,
      chatId,
      systemEntryId,
      name,
      sourceFileName,
      files,
      apps,
    },
  }) => {
    const { userId } = ctx;

    const paths = Object.keys(files);
    if (!paths.some((p) => p.endsWith("/config.toml"))) {
      throw userFacingError("Zip does not contain any step config.toml files");
    }

    const now = Date.now();
    const hasApps = apps.length > 0;

    const siblings = await tx.run(
      zql.workflows.where("spaceId", space.id).where("deletedAt", "IS", null)
    );
    const uniqueName = uniqueWorkflowName(
      sanitizeWorkflowName(name),
      siblings.map((w) => w.name)
    );

    await tx.mutate.workflows.insert({
      id: workflowId,
      spaceId: space.id,
      name: uniqueName,
      needsName: false,
      createdAt: now,
      updatedAt: now,
      createdByUserId: userId,
      autofixEnabled: true,
      autoImproveEnabled: false,
      retentionSeconds: DEFAULT_WORKFLOW_RETENTION_SECONDS,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.spaces.update({
      id: space.id,
      updatedAt: now,
    });

    await tx.mutate.workflowBranches.insert({
      id: branchId,
      workflowId,
      userId,
      name: "First draft",
      headCommitId: commitId,
      createdAt: now,
      lastChatEntryAt: now,
      lastChatEntryType: "SystemMessage",
      ...(hasApps && { respondingAt: now }),
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowCommits.insert({
      id: commitId,
      branchId,
      parentCommitId: null,
      chatId,
      message: null,
      createdAt: now,
      createdByUserId: userId,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.chats.insert({
      id: chatId,
      commitId,
      createdAt: now,
      lastChatEntryType: "SystemMessage",
      ...(hasApps && { respondingAt: now }),
      tenantId: ctx.tenantId,
    });

    await tx.mutate.chatEntries.insert({
      id: systemEntryId,
      chatId,
      entryType: "SystemMessage",
      createdAt: now,
      previousEntryId: null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.systemMessages.insert({
      entryId: systemEntryId,
      text: `Imported from ${sourceFileName}`,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.branchContributors.insert({
      branchId,
      userId,
      tenantId: ctx.tenantId,
    });

    const c = { tx, commitId, tenantId: ctx.tenantId };
    for (const [path, content] of Object.entries(files)) {
      await writeCommitFile(c, path, content);
    }
    await syncAllFacets(c);
  },
});
